/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Button, breakpoints } from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import type { ButtonName } from '@coursera/event-pulse-types';
import { useTracker } from '@coursera/event-pulse/react';

import HeroModule from 'bundles/cds-labs/components/Modules/HeroModule';
import { MODULE_LAYOUTS } from 'bundles/cds-labs/components/Modules/ModuleLayout';
import type { MarketingContentFieldsFragment } from 'bundles/front-page/__generated__/contentfulTypes';
import SeoPageHeader from 'bundles/front-page/components/modules/hero-banner/SeoPageHeader';

import _t from 'i18n!nls/front-page';

type HeroModuleProps = React.ComponentProps<typeof HeroModule>;

type Props = {
  content?: MarketingContentFieldsFragment | null;
  secondaryButtonLabel?: string | null;
  secondaryButtonLink?: string | null;
};

const styles = {
  container: css`
    ${breakpoints.down('xs')} {
      text-align: center;
    }
  `,
  description: css`
    margin-bottom: var(--cds-spacing-200);
    white-space: pre-line;

    ${breakpoints.up('md')} {
      width: 80%;
    }
  `,
  button: css`
    ${breakpoints.down('xs')} {
      width: 100%;
    }
  `,
  hidden: css`
    display: none;
  `,
};

function MerchandisingHeroModuleRenderer({ content, secondaryButtonLabel, secondaryButtonLink }: Props) {
  const track = useTracker();

  if (!content) {
    return null;
  }

  const { description, callToActionLabel, callToActionLink, visual, title } = content || {};

  const trackClick = (buttonName: ButtonName, linkName: string, linkTarget: string) => () => {
    track('click_button', {
      button: { name: buttonName, linkName, linkURL: linkTarget, linkType: 'internal' },
      pageSection: {
        sectionName: 'hero_banner',
      },
    });
  };

  const descriptionToShow = (descriptionCopy?: string | null) => (
    <p css={styles.description}>{descriptionCopy ?? ''}</p>
  );

  const getHeroModuleProps = (): HeroModuleProps => {
    let imageHeight = visual?.height;
    let imageWidth = visual?.width;

    if (imageHeight && imageWidth) {
      imageWidth = 720;
      imageHeight = (imageHeight / imageWidth) * 720;
    }

    return {
      headline: title,
      headlineComponent: 'h2',
      description: descriptionToShow(description),
      image: {
        url: visual?.url,
        description: visual?.description,
        width: visual?.width,
        height: visual?.height,
      },
      layout: MODULE_LAYOUTS.text6image6,
      showImagesAboveContentOnMobile: false,
      preloadImage: true,
      customPrimaryCta:
        callToActionLabel && callToActionLink ? (
          <Button
            component="a"
            href={callToActionLink}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            css={styles.button}
            onClick={trackClick('hero_primary_cta', callToActionLabel, callToActionLink)}
          >
            {callToActionLabel}
          </Button>
        ) : undefined,
      customSecondaryCta:
        secondaryButtonLabel && secondaryButtonLink ? (
          <Button
            variant="secondary"
            component="a"
            href={secondaryButtonLink}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            css={styles.button}
            onClick={trackClick('hero_secondary_cta', secondaryButtonLabel, secondaryButtonLink)}
          >
            {secondaryButtonLabel}
          </Button>
        ) : undefined,
    };
  };

  return (
    <div css={styles.container}>
      <SeoPageHeader />
      <HeroModule {...getHeroModuleProps()} />
      <div css={styles.hidden}>
        <div>{_t('Save big, dream bigger with Coursera Plus')}</div>
        <div>
          {_t(
            'Get $160 off a year of unlimited access to programs from Google, Microsoft, IBM, and more—all in one subscription.'
          )}
        </div>
        <div>
          {_t(
            'Get $160 off a year of unlimited access to programs from Google, IBM, and more—all in one subscription.'
          )}
        </div>
        <div>{_t('Save $160 now')}</div>
        <div>{_t('Unwrap new career skills. Coursera Plus for INR 7999.')}</div>
        <div>
          {_t(
            'Get unlimited access to 7,000+ learning programs from Google, Microsoft, IBM, and more, for just INR 7999.'
          )}
        </div>
        <div>{_t('Claim your offer')}</div>
        <div>
          {_t(
            'Get a year of unlimited access to programs like Google, Microsoft, IBM, and more—all in one subscription, for 65% off.'
          )}
        </div>
        <div>{_t('Save today')}</div>
        <div>
          {_t(
            'Get a year of unlimited access to programs like Google, Microsoft, IBM, and more—all in one subscription, for €145 off.'
          )}
        </div>
        <div>{_t('Hurry…our Black Friday sales ends tomorrow')}</div>
        <div>
          {_t(
            'Get $160 off Coursera Plus annual and gain career-changing skills in GenAI, data analytics, cybersecurity and more.'
          )}
        </div>
        <div>{_t('Get $160 off Coursera Plus annual')}</div>
        <div>
          {_t(
            "It's the last day to give your career the gift that keeps on giving: unlimited access to over 7,000 courses from Meta, Microsoft, Google, and more."
          )}
        </div>
        <div>{_t('Get $160 off Coursera Plus annual and skill up in GenAI, data analytics, and more.')}</div>
        <div>
          {_t(
            "It's the last day to give your career the gift of unlimited access to 7,000+ courses from Meta, Google, and more."
          )}
        </div>
        <div>
          {_t(
            'Get Coursera Plus annual for INR 7999 and gain career-changing skills in GenAI, data analytics, cybersecurity and more.'
          )}
        </div>
        <div>{_t('Get Coursera Plus annual for INR 7999')}</div>
        <div>{_t('Claim now')}</div>
        <div>
          {_t(
            'Get Coursera Plus annual at €145 off and gain career-changing skills in GenAI, data analytics, cybersecurity and more.'
          )}
        </div>
        <div>{_t('Get Coursera Plus annual for €145 off')}</div>
        <div>{_t('Save now')}</div>
        <div>
          {_t(
            'Get Coursera Plus annual at 65% off and gain career-changing skills in GenAI, data analytics, cybersecurity and more. '
          )}
        </div>
        <div>{_t('Get Coursera Plus annual for 65% off')}</div>
      </div>
    </div>
  );
}

export default MerchandisingHeroModuleRenderer;
